import * as Sentry from '@sentry/react';
import { action, computed, makeObservable, observable, runInAction } from 'mobx';

import { AUTH_MODES, AuthMode } from '@/configs/authMode';
import { ENDPOINTS } from '@/configs/endpoints';
import { KRATOS_UI_URL, USE_KRATOS_API } from '@/configs/env';
import { ROUTES } from '@/configs/routes';
import { Session, SessionSchema, SessionApi } from '@/entities/Session';
import { ILocalStore } from '@/stores/interfaces/ILocalStore';
import { MetaStore } from '@/stores/local/shared/MetaStore';
import call from '@/utils/api';
import { schemeParse } from '@/utils/schemeParse';

export class SessionStore implements ILocalStore {
  readonly loadSessionMeta = new MetaStore();

  session: Session | null = null;

  redirectToLoginUrl: string | null = null;

  authMode: AuthMode | null = null;

  constructor() {
    makeObservable(this, {
      session: observable.ref,
      setSession: action.bound,
      userId: computed,
      givenName: computed,
      middleName: computed,
      familyName: computed,
      displayName: computed,
      fullName: computed,
      avatarLetter: computed,
      isAuthenticated: computed,
      isAlfaIdMode: computed,
      isPhoneTotpOnlyMode: computed,
      loadSession: action.bound,
      logout: action.bound,
      redirectToLoginUrl: observable,
      authMode: observable,
    });
  }

  setSession(session: Session | null) {
    this.session = session;
  }

  get userId(): string | null {
    return this._userId;
  }

  private get _userId(): string | null {
    const traitsId = this._normalizeName(this.session?.identity.traits.id);

    if (traitsId) {
      return traitsId;
    }

    if (this.session?.identity.id) {
      return this.session.identity.id;
    }

    return this.session?.id ?? null;
  }

  private _normalizeName(value?: string | null): string | null {
    if (!value) {
      return null;
    }

    const trimmed = value.trim();

    return trimmed.length > 0 ? trimmed : null;
  }

  get givenName(): string | null {
    return this._normalizeName(this.session?.identity.traits.givenName);
  }

  get middleName(): string | null {
    return this._normalizeName(this.session?.identity.traits.middleName);
  }

  get familyName(): string | null {
    return this._normalizeName(this.session?.identity.traits.familyName);
  }

  get displayName(): string {
    const givenName = this.givenName;
    const familyName = this.familyName;

    if (givenName && familyName) {
      return `${givenName} ${familyName[0].toUpperCase()}.`;
    }

    if (givenName) {
      return givenName;
    }

    const fallbackId = this._userId;

    return fallbackId ? `Пользователь №${fallbackId}` : 'Пользователь';
  }

  get fullName(): string {
    const parts = [this.givenName, this.middleName, this.familyName]
      .map((item) => item?.trim())
      .filter(Boolean) as string[];

    if (parts.length > 0) {
      return parts.join(' ');
    }

    const fallbackId = this._userId;

    return fallbackId ? `Пользователь №${fallbackId}` : 'Пользователь';
  }

  get avatarLetter(): string | null {
    const givenName = this.givenName;

    if (givenName) {
      return givenName[0]?.toUpperCase() ?? null;
    }

    const firstLetter = this.displayName[0];

    return firstLetter ? firstLetter.toUpperCase() : null;
  }

  get isAuthenticated() {
    return !!this.session;
  }

  get isAlfaIdMode() {
    return this.authMode === AUTH_MODES.ALFA_ID;
  }

  get isPhoneTotpOnlyMode() {
    return this.authMode === AUTH_MODES.PHONE_TOTP_ONLY;
  }

  async loadSession(): Promise<void> {
    if (this.loadSessionMeta.isStaleLoaded || this.session) {
      return;
    }

    this.loadSessionMeta.setLoadedStartMeta();

    const response = await call<SessionApi | { redirect_url: string } | { auth_mode: AuthMode }>({
      endpoint: USE_KRATOS_API ? ENDPOINTS.sessions.whoamiKratos : ENDPOINTS.sessions.whoami,
    });

    if (response.isError) {
      runInAction(() => {
        this.loadSessionMeta.setLoadedErrorMeta();

        if (USE_KRATOS_API) {
          this.redirectToLoginUrl = `${KRATOS_UI_URL}/login?return_url=${window.location.href}`;
        }
      });

      return;
    }

    if (!USE_KRATOS_API && 'auth_mode' in response.data) {
      const { auth_mode: authMode } = response.data;

      runInAction(() => {
        this.authMode = authMode;
        this.loadSessionMeta.setLoadedErrorMeta();
      });

      return;
    }

    /**
     * Если используется не кратос то получаем redirect_url из ответа
     */
    if (!USE_KRATOS_API && 'redirect_url' in response.data) {
      const { redirect_url: redirectUrl } = response.data;

      runInAction(() => {
        this.redirectToLoginUrl = redirectUrl;
        this.loadSessionMeta.setLoadedErrorMeta();
      });

      return;
    }

    const parsed = schemeParse(SessionSchema, response.data);

    if (!parsed.success) {
      this.loadSessionMeta.setLoadedErrorMeta();

      return;
    }

    this.setSession(parsed.data);
    const userEmail = parsed.data.identity.traits.email ?? parsed.data.identity.traits.phone ?? parsed.data.identity.id;
    const userId = parsed.data.identity.traits.id ?? parsed.data.identity.id ?? parsed.data.id;

    Sentry.setUser({ id: userId, email: userEmail });
    runInAction(() => {
      this.loadSessionMeta.setLoadedSuccessMeta();
    });
  }

  async logout(): Promise<void> {
    const response = await call<{ redirect_url: string }>({
      endpoint: ENDPOINTS.auth.logout,
      method: 'POST',
    });

    if (response.isError) {
      this.setSession(null);
      Sentry.setUser(null);

      if (!this.redirectToLoginUrl && USE_KRATOS_API) {
        this.redirectToLoginUrl = `${KRATOS_UI_URL}/login?return_url=${window.location.href}`;
      }

      window.location.href = ROUTES.root.path;

      return;
    }

    if (response.data?.redirect_url) {
      this.redirectToLoginUrl = response.data.redirect_url;
    } else if (!this.redirectToLoginUrl && USE_KRATOS_API) {
      this.redirectToLoginUrl = `${KRATOS_UI_URL}/login?return_url=${window.location.href}`;
    }
    this.setSession(null);
    Sentry.setUser(null);
    window.location.href = ROUTES.root.path;
  }

  destroy(): void {
    this.loadSessionMeta.destroy();
  }
}
